///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import {
  getGoTrackUser,
  createNewUser,
  putLastActiveDate,
  putUsersAssociatedCarriers,
} from "../api/api";
import { isCarrier } from "../helpers/goTrackUserUtils";
import types from "../helpers/types";
import { setUserContext } from "../telemetryService";

export const initialState = {
  fetchRequestStatus: types.requestStatuses.initial,
  isError: false,
  goTrackUser: null,
  isCreatingUser: false,
  updateLastActiveDateRequestStatus: types.requestStatuses.initial,
  sessionHasEnded: false,
};

const goTrackUserSlice = createSlice({
  name: "goTrackUserSlice",
  initialState,
  reducers: {
    requestGoTrackUser: (state) => {
      state.fetchRequestStatus = types.requestStatuses.inProgress;
    },
    receiveGoTrackUserSuccess: (state, { payload }) => {
      state.goTrackUser = payload;
      state.fetchRequestStatus = types.requestStatuses.success;
    },
    receiveGoTrackUserFailure: (state) => {
      state.fetchRequestStatus = types.requestStatuses.failure;
      state.isError = true;
    },
    requestCreateGoTrackUser: (state) => {
      state.isCreatingUser = true;
    },
    receiveCreateGoTrackUserSuccess: (state) => {
      state.isCreatingUser = false;
    },
    receiveCreateGoTrackUserFailure: (state) => {
      state.isCreatingUser = false;
    },
    requestUpdateLastActiveDate: (state) => {
      state.updateLastActiveDateRequestStatus =
        types.requestStatuses.inProgress;
    },
    receiveUpdateLastActiveDateSuccess: (state) => {
      state.updateLastActiveDateRequestStatus = types.requestStatuses.success;
    },
    receiveUpdateLastActiveDateFailure: (state) => {
      state.updateLastActiveDateRequestStatus = types.requestStatuses.failure;
    },
    endSession: (state) => {
      state.sessionHasEnded = true;
    },
  },
});

export const {
  requestGoTrackUser,
  receiveGoTrackUserSuccess,
  receiveGoTrackUserFailure,
  requestCreateGoTrackUser,
  receiveCreateGoTrackUserSuccess,
  receiveCreateGoTrackUserFailure,
  requestUpdateLastActiveDate,
  receiveUpdateLastActiveDateSuccess,
  receiveUpdateLastActiveDateFailure,
  endSession,
} = goTrackUserSlice.actions;

export const fetchGoTrackUser = (successCallback) => async (dispatch) => {
  let goTrackUser;
  dispatch(requestGoTrackUser());
  try {
    goTrackUser = await getGoTrackUser();
    setUserContext(goTrackUser);
  } catch {
    dispatch(receiveGoTrackUserFailure());
    return;
  }
  dispatch(receiveGoTrackUserSuccess(goTrackUser));  
  successCallback(goTrackUser);
};

export const createGoTrackUser =
  (user, successCallback, associatedCarrierList) => async (dispatch) => {
    dispatch(requestCreateGoTrackUser());
    try {
      const { data } = await createNewUser(user);
      if (isCarrier([user.role])) {
        await putUsersAssociatedCarriers(data, associatedCarrierList);
      }
    } catch (e) {
      dispatch(receiveCreateGoTrackUserFailure());
      return;
    }

    dispatch(receiveCreateGoTrackUserSuccess());
    successCallback();
  };

export const updateLastActiveDate = () => async (dispatch) => {
  dispatch(requestUpdateLastActiveDate());
  try {
    await putLastActiveDate();
  } catch (e) {
    dispatch(receiveUpdateLastActiveDateFailure());
    return;
  }
  dispatch(receiveUpdateLastActiveDateSuccess());
};

export const goTrackUserReducer = goTrackUserSlice.reducer;
